import React from 'react';
import styled from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import {
  AccountCircleOutlined as AccountCircle,
  ArrowDropDown,
  ExitToApp,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Background, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { GENERAL_CONSTANTS } from 'constants/general';

const AdminPanel = styled.div`
  padding: ${Space.spBase} ${Space.sp2x};
  border-radius: 4px;
  min-width: 157px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  margin-left: auto;
`;

const UserInfo = styled(FlexboxContainer)`
  align-items: flex-start;
  flex-direction: column;
  gap: ${Space.spHalf};
`;

const UserNameText = styled.div`
  color: ${Text.primary};
  font-size: 14px;
  font-weight: 700;
  padding: 0;
  margin: 0;
`;

const UserRoleText = styled.div`
  color: ${Text.primary};
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  margin: 0;
`;

const UserModal = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 158px;
  background: white;
  border-radius: 6px;
  z-index: 1001;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const UserModalItem = styled.div`
  padding: 10px ${Space.sp3x};
  display: flex;
  align-items: center;
  gap: ${Space.spBase};
  cursor: pointer;
  
  &:hover {
    background: ${Background.shaded};
  }
  
  &:last-child {
    border-top: 1px solid #C1C9D1;
    padding-top: ${Space.sp2x}
  }
`;

const UserModalText = styled.div`
  color: ${Text.primary};
  font-size: 14px;
  font-weight: 500;
`;

interface UserProfileMenuProps {
  userName: string;
  displayUserRole: string;
  isUserModalOpen: boolean;
  onToggleModal: () => void;
  onAccountClick: () => void;
  onSettingsClick: () => void;
  onLogout: () => void;
}

/**
 * User profile menu component with dropdown functionality
 * 
 * Features:
 * - User name and role display
 * - Dropdown menu with account, settings, logout options
 * - Click outside to close functionality
 * - Proper accessibility and keyboard navigation
 */
const UserProfileMenu: React.FC<UserProfileMenuProps> = React.memo(({
  userName,
  displayUserRole,
  isUserModalOpen,
  onToggleModal,
  onAccountClick,
  onSettingsClick,
  onLogout
}) => {
  return (
    <AdminPanel className="admin-panel" onClick={onToggleModal}>
      <UserInfo displayFlex="flex" gap='0px'>
        <UserNameText>{userName}</UserNameText>
        <UserRoleText>{displayUserRole}</UserRoleText>
      </UserInfo>

      <ArrowDropDown style={{ color: Text.primary, fontSize: '28px', paddingLeft: '8px' }} />

      {isUserModalOpen && (
        <UserModal>
          <UserModalItem onClick={onAccountClick}>
            <AccountCircle />
            <UserModalText>{GENERAL_CONSTANTS.ACCOUNT}</UserModalText>
          </UserModalItem>
          <UserModalItem onClick={onSettingsClick}>
            <SettingsIcon />
            <UserModalText>{GENERAL_CONSTANTS.SETTINGS}</UserModalText>
          </UserModalItem>
          <UserModalItem onClick={onLogout}>
            <ExitToApp />
            <UserModalText>{GENERAL_CONSTANTS.LOGOUT}</UserModalText>
          </UserModalItem>
        </UserModal>
      )}
    </AdminPanel>
  );
});

UserProfileMenu.displayName = 'UserProfileMenu';

export default UserProfileMenu;